import { JobETAState } from '@treadinc/horizon-api-spec';
import { Dayjs } from 'dayjs';

import { JobEtaBasic } from '~hooks/useJob/models';

// Dwell thresholds for flagging a truck that has been sitting on site too long. Pilot
// defaults chosen with dispatch; there is no backend-computed dwell allowance to read.
const DWELL_WARNING_SECONDS = 30 * 60;
const DWELL_CRITICAL_SECONDS = 60 * 60;

export type EtaLeg = 'pickup' | 'dropoff';

/** How prominently the ETA line reads; `muted` carries no number. */
export type EtaTone = 'muted' | 'normal' | 'warning' | 'critical';

/** `null` for the states that name no site: `pending` and `completed`. */
export function etaLegForState(state: JobETAState) {
  if (state === JobETAState.TO_PICKUP || state === JobETAState.ARRIVED_PICKUP) {
    return 'pickup';
  }

  if (state === JobETAState.TO_DROPOFF || state === JobETAState.ARRIVED_DROPOFF) {
    return 'dropoff';
  }

  return null;
}

export function isEnRouteEtaState(state: JobETAState) {
  return state === JobETAState.TO_PICKUP || state === JobETAState.TO_DROPOFF;
}

/** Compact, column-friendly duration: `<1m`, `8m`, `1h2m`. */
export function formatCompactDuration(totalSeconds: number) {
  const wholeSeconds = Math.max(0, Math.floor(totalSeconds));

  if (wholeSeconds < 60) return '<1m';

  const totalMinutes = Math.floor(wholeSeconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours === 0) return `${minutes}m`;

  return `${hours}h${minutes}m`;
}

export function dwellTone(elapsedSeconds: number) {
  if (elapsedSeconds >= DWELL_CRITICAL_SECONDS) return 'critical';
  if (elapsedSeconds >= DWELL_WARNING_SECONDS) return 'warning';

  return 'normal';
}

/**
 * How severe a live ETA reads. Shared by the cell's text color and the row tint so the two
 * can never disagree; `null` means the ETA says nothing worth showing.
 */
export function etaTone(eta: JobEtaBasic, now: Dayjs) {
  if (!etaLegForState(eta.state)) return null;

  if (eta.idleAt) return dwellTone(now.diff(eta.idleAt, 'second'));

  if (eta.etaAt) return eta.etaAt.diff(now, 'second') < 0 ? 'critical' : 'normal';

  return isEnRouteEtaState(eta.state) ? 'muted' : null;
}
